import React from 'react';
import { Flame, Check, Sparkles, Clock, DollarSign } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * FilterChips - Barre de filtres décisionnels pour mobile
 * 
 * Props:
 * - activeFilter: filtre actuellement actif
 * - onFilterChange: callback quand on change de filtre
 * - counts: objet avec les compteurs par filtre
 *   { priority: 12, recommended: 8, new: 3, budgetOk: 6, all: 24 }
 */
const FilterChips = ({ activeFilter, onFilterChange, counts = {} }) => {
  
  const filters = [
    {
      id: 'priority',
      label: 'Prioritaires',
      icon: Flame,
      color: 'orange',
      count: counts.priority || 0
    },
    {
      id: 'recommended',
      label: 'Recommandés',
      icon: Check,
      color: 'green',
      count: counts.recommended || 0
    },
    {
      id: 'new',
      label: 'Nouveaux',
      icon: Sparkles,
      color: 'blue',
      count: counts.new || 0
    },
    {
      id: 'budgetOk',
      label: 'Budget OK',
      icon: DollarSign,
      color: 'emerald',
      count: counts.budgetOk || 0
    },
    {
      id: 'all',
      label: 'Tous',
      icon: null,
      color: 'gray',
      count: counts.all || 0
    }
  ];

  const getActiveStyles = (filter) => {
    if (activeFilter !== filter.id) {
      return 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50';
    }
    
    switch (filter.color) {
      case 'orange':
        return 'bg-[#0F2744] border-[#0F2744] text-white';
      case 'green':
        return 'bg-[#4C8F6E] border-[#4C8F6E] text-white';
      case 'blue':
        return 'bg-[#0F2744]/80 border-[#0F2744]/80 text-white';
      case 'emerald':
        return 'bg-[#4C8F6E]/80 border-[#4C8F6E]/80 text-white';
      default:
        return 'bg-gray-700 border-gray-700 text-white';
    }
  };

  const getIconColor = (filter) => {
    if (activeFilter === filter.id) return 'text-white';
    
    switch (filter.color) {
      case 'orange':
        return 'text-[#0F2744]';
      case 'green':
        return 'text-[#4C8F6E]';
      case 'blue':
        return 'text-[#0F2744]';
      case 'emerald':
        return 'text-[#4C8F6E]';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div 
      className="flex gap-2 overflow-x-auto pb-2 scrollbar-none"
      data-testid="filter-chips"
    >
      {filters.map((filter) => (
        <button
          key={filter.id}
          onClick={() => onFilterChange(activeFilter === filter.id ? 'all' : filter.id)}
          className={cn(
            "flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-medium whitespace-nowrap transition-all",
            getActiveStyles(filter)
          )}
          data-testid={`filter-chip-${filter.id}`}
        >
          {filter.icon && (
            <filter.icon className={cn("h-4 w-4", getIconColor(filter))} />
          )}
          {filter.label}
          {filter.count > 0 && (
            <span className={cn(
              "ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold",
              activeFilter === filter.id 
                ? "bg-white/20 text-white" 
                : "bg-gray-100 text-gray-600"
            )}>
              {filter.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

export default FilterChips;
